/**
 * Utilidad para servir versiones optimizadas de imágenes de Supabase Storage
 * en listas/grids, manteniendo la URL original para modales y "ver en tamaño completo".
 *
 * Las transformaciones usan Supabase Image Transformations (imgproxy):
 * - Redimensionado, formato WebP automático, control de calidad.
 * - Solo aplica a URLs de Supabase Storage; el resto se devuelve sin cambios.
 * - Requiere transformaciones de imagen en el proyecto Supabase (habitualmente plan Pro+).
 *   Sin ellas, desactiva con EXPO_PUBLIC_USE_IMAGE_TRANSFORMS=false.
 */

export type ImageDisplayPreset =
  | 'thumbnail'
  | 'listThumb'
  | 'listLogo'
  | 'card'
  | 'logo'
  | 'cover'
  | 'hero'
  | 'heroMobile';

/** Presets orientados a tráfico público (catálogo, votación). Menos egress en CDN. */
const PRESETS: Record<ImageDisplayPreset, { width: number; height: number; resize?: 'cover' | 'contain' | 'fill' }> = {
  /** Grids de propuestas / listados densos */
  listThumb: { width: 320, height: 320, resize: 'cover' },
  /** Círculo del listado de restaurantes (~30–40 px en pantalla, 2x). */
  listLogo: { width: 64, height: 64, resize: 'cover' },
  thumbnail: { width: 400, height: 400, resize: 'cover' },
  card: { width: 400, height: 267, resize: 'cover' },
  logo: { width: 96, height: 96, resize: 'cover' },
  /** Ficha de votación (una sola propuesta) */
  cover: { width: 560, height: 315, resize: 'cover' },
  hero: { width: 960, height: 480, resize: 'cover' },
  heroMobile: { width: 640, height: 800, resize: 'cover' },
};

const DEFAULT_TRANSFORM_QUALITY = 72;

export interface ImageDisplayOptions {
  width?: number;
  height?: number;
  resize?: 'cover' | 'contain' | 'fill';
  quality?: number;
  preset?: ImageDisplayPreset;
}

/**
 * Comprueba si la URL es de Supabase Storage (object/public).
 */
export function isSupabaseStorageUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  const u = url.trim();
  return u.includes('supabase.co/storage') && u.includes('/object/public/');
}

/**
 * Devuelve la URL original sin transformar. Útil para modales, "ver en tamaño completo" o descarga.
 */
export function getImageOriginalUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') return null;
  return url.replace(/^http:/, 'https:').trim() || null;
}

const envTransforms = typeof process !== 'undefined' ? process.env.EXPO_PUBLIC_USE_IMAGE_TRANSFORMS : undefined;
/** Activas por defecto (menos bytes por vista). Plan Free: poner EXPO_PUBLIC_USE_IMAGE_TRANSFORMS=false si las URLs /render devuelven 404. */
const USE_TRANSFORMS =
  typeof process !== 'undefined' &&
  envTransforms !== 'false' &&
  envTransforms !== '0' &&
  envTransforms !== 'off';

/**
 * Devuelve una URL optimizada para mostrar en listas/grids (transformada).
 * Si no es Supabase Storage, devuelve la URL tal cual.
 * Para ver la imagen original, usa la URL guardada en BD o getImageOriginalUrl.
 *
 * Requiere Supabase con transformaciones de imagen (plan Pro o superior).
 * Desactivar: EXPO_PUBLIC_USE_IMAGE_TRANSFORMS=false
 */
export function getImageDisplayUrl(
  url: string | null | undefined,
  options: ImageDisplayOptions = {}
): string | null {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim().replace(/^http:/, 'https:');
  if (!u) return null;

  if (!isSupabaseStorageUrl(u)) return u;

  if (!USE_TRANSFORMS) return u;

  const lower = u.toLowerCase();
  if (lower.endsWith('.svg')) return u;

  const preset = options.preset ? PRESETS[options.preset] : null;
  const width = options.width ?? preset?.width ?? 480;
  const height = options.height ?? preset?.height ?? 480;
  const resize = options.resize ?? preset?.resize ?? 'cover';
  const quality = Math.min(100, Math.max(20, options.quality ?? DEFAULT_TRANSFORM_QUALITY));

  const base = u.replace('/object/public/', '/render/image/public/');
  const sep = base.includes('?') ? '&' : '?';
  const params = new URLSearchParams();
  params.set('width', String(width));
  params.set('height', String(height));
  params.set('resize', resize);
  params.set('quality', String(quality));

  return `${base}${sep}${params.toString()}`;
}
